import type {SearchResults} from '../types'
import {
  getValidCaseInsensitiveOption,
  getFilterValuesFromParsedQuery,
  getValidFilterValueFromParsedQuery,
} from './filters'

export const allTasksOptionID = 'all'
export const taskOptions = [
  {id: allTasksOptionID, name: 'All'},
  {id: 'chat-completion', name: 'Chat/completion'},
  {id: 'embeddings', name: 'Embeddings'},
]
export function selectedTaskName(rawTask: string | undefined | null) {
  const normalizedTask = rawTask?.toLowerCase() ?? allTasksOptionID
  const selectedOption = taskOptions.find(option => option.id === normalizedTask)
  return selectedOption?.name ?? rawTask
}
export const inputModalityOptions = ['audio', 'image', 'text']
export const outputModalityOptions = ['embeddings', 'text']
export const licenseOptions = [
  {id: 'custom', name: 'Custom'},
  {id: 'mit', name: 'MIT'},
]
export const allPublishersOption = 'All'
export const allCategoriesOptionID = 'All'
export const languageOptions = [
  {id: 'af', name: 'Afrikaans'},
  {id: 'ar', name: 'Arabic'},
  {id: 'bn', name: 'Bengali'},
  {id: 'zh', name: 'Chinese'},
  {id: 'zh-cn', name: 'Chinese (Simplified)'},
  {id: 'cs', name: 'Czech'},
  {id: 'da', name: 'Danish'},
  {id: 'nl', name: 'Dutch'},
  {id: 'en', name: 'English'},
  {id: 'eo', name: 'Esperanto'},
  {id: 'et', name: 'Estonian'},
  {id: 'fi', name: 'Finnish'},
  {id: 'fr', name: 'French'},
  {id: 'de', name: 'German'},
  {id: 'el', name: 'Greek'},
  {id: 'gu', name: 'Gujarati'},
  {id: 'ha', name: 'Hausa'},
  {id: 'he', name: 'Hebrew'},
  {id: 'hi', name: 'Hindi'},
  {id: 'hu', name: 'Hungarian'},
  {id: 'is', name: 'Icelandic'},
  {id: 'id', name: 'Indonesian'},
  {id: 'it', name: 'Italian'},
  {id: 'ja', name: 'Japanese'},
  {id: 'jv', name: 'Javanese'},
  {id: 'kn', name: 'Kannada'},
  {id: 'ko', name: 'Korean'},
  {id: 'lv', name: 'Latvian'},
  {id: 'ml', name: 'Malayalam'},
  {id: 'mr', name: 'Marathi'},
  {id: 'ne', name: 'Nepali'},
  {id: 'no', name: 'Norwegian'},
  {id: 'or', name: 'Odia'},
  {id: 'ps', name: 'Pashto'},
  {id: 'fa', name: 'Persian'},
  {id: 'pl', name: 'Polish'},
  {id: 'pt-br', name: 'Portuguese (Brazil)'},
  {id: 'pt', name: 'Portuguese'},
  {id: 'pa', name: 'Punjabi'},
  {id: 'ro', name: 'Romanian'},
  {id: 'ru', name: 'Russian'},
  {id: 'es', name: 'Spanish'},
  {id: 'sw', name: 'Swahili'},
  {id: 'sv', name: 'Swedish'},
  {id: 'tl', name: 'Tagalog'},
  {id: 'ta', name: 'Tamil'},
  {id: 'te', name: 'Telugu'},
  {id: 'th', name: 'Thai'},
  {id: 'tr', name: 'Turkish'},
  {id: 'uk', name: 'Ukrainian'},
  {id: 'ur', name: 'Urdu'},
  {id: 'vi', name: 'Vietnamese'},
  {id: 'cy', name: 'Welsh'},
  {id: 'yo', name: 'Yoruba'},
]
export const sortOptions: Record<string, string> = {
  'name-asc': 'Alphabetical',
  'created-desc': 'Recently added',
  'output-tokens-desc': 'Output token limit',
  'input-tokens-desc': 'Input token limit',
  popularity: 'Popularity',
}

export function getCategoryDisplayName(category: string | null | undefined) {
  if (typeof category !== 'string') return allCategoriesOptionID
  const trimmedCategory = category.trim()
  if (trimmedCategory.length < 1) return allCategoriesOptionID
  switch (trimmedCategory.toLocaleLowerCase()) {
    case 'rag':
      return 'RAG'
    default:
      return trimmedCategory.charAt(0).toUpperCase() + trimmedCategory.slice(1)
  }
}

// Keep in sync with `DEFAULT_MODELS_SORT_VALUE` in app/helpers/marketplace_helper.rb
export const defaultSortValue = 'created-desc'

export function getDefaultSort(sortValue: string | undefined, parsedQuery: SearchResults['parsedQuery']) {
  const validOptions = Object.keys(sortOptions)
  const sortFromParsedQuery = getValidFilterValueFromParsedQuery('sort', parsedQuery, validOptions)
  if (sortFromParsedQuery) return sortFromParsedQuery
  return getValidCaseInsensitiveOption(sortValue, validOptions) || defaultSortValue
}

export function getDefaultCategory(
  searchParams: URLSearchParams,
  parsedQuery: SearchResults['parsedQuery'],
  categoryOptions: string[],
) {
  if (categoryOptions.includes(searchParams.get('category') || '')) {
    return searchParams.get('category')
  }
  const categoryFromParsedQuery = getValidFilterValueFromParsedQuery('category', parsedQuery, categoryOptions)
  if (categoryFromParsedQuery) return categoryFromParsedQuery
  return null
}

export function getDefaultTask(searchParams: URLSearchParams, parsedQuery: SearchResults['parsedQuery']) {
  const taskFromParams = searchParams.get('task') || ''
  const validOptions = taskOptions.map(({id}) => id)
  if (validOptions.includes(taskFromParams.toLowerCase())) return taskFromParams

  const taskFromParsedQuery = getValidFilterValueFromParsedQuery('task', parsedQuery, validOptions)
  return taskFromParsedQuery ?? allTasksOptionID
}

export function getDefaultPublisher(
  searchParams: URLSearchParams,
  parsedQuery: SearchResults['parsedQuery'],
  publisherOptions: string[],
) {
  const publisherFromParams = searchParams.get('publisher') || ''
  for (const option of publisherOptions) {
    if (option.toLowerCase() === publisherFromParams.toLowerCase()) return option
  }

  const filterValues = getFilterValuesFromParsedQuery('publisher', parsedQuery)
  for (const filterValue of filterValues) {
    const publisherFromParsedQuery = publisherOptions.find(opt => filterValue.toLowerCase() === opt.toLowerCase())
    if (publisherFromParsedQuery) return publisherFromParsedQuery
  }

  return allPublishersOption
}

export function getValidCategoryFromParsedQuery(parsedQuery: SearchResults['parsedQuery'], categoryOptions: string[]) {
  return getValidFilterValueFromParsedQuery('category', parsedQuery, categoryOptions)
}

export function getValidTaskFromParsedQuery(parsedQuery: SearchResults['parsedQuery']) {
  return getValidFilterValueFromParsedQuery(
    'task',
    parsedQuery,
    taskOptions.map(option => option.id),
  )
}

export function getValidPublisherFromParsedQuery(
  parsedQuery: SearchResults['parsedQuery'],
  publisherOptions: string[],
) {
  return getValidFilterValueFromParsedQuery('publisher', parsedQuery, publisherOptions)
}

export function getValidSortFromParsedQuery(parsedQuery: SearchResults['parsedQuery']) {
  return getValidFilterValueFromParsedQuery('sort', parsedQuery, Object.keys(sortOptions))
}
